<script setup lang="ts">
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import type HotelPackage from '@itinerary/hotel/HotelPackage';
import { type HotelPriceObject } from '@itinerary/hotel/HotelModuleTypes';

import DatepickerCalendar from '@components/base/mr-datepicker/DatepickerCalendar.vue';
import type { DayConfig } from '#imports';

const { roomId } = defineProps<{
    roomId: string;
}>();
const emit = defineEmits<{
    close: [];
}>();
const hotelModule = useHotelModule();
const packageSelectStore = usePackageSelectStore();

const calendarType = ref<'jalali' | 'gregorian'>('jalali');
const prices = ref<HotelPriceObject>({});
const loadingPrice = ref(false);
const currentCalendar = computed(() => {
    switch (calendarType.value) {
        case 'jalali':
            return 0;
        case 'gregorian':
            return 1;
        default:
            return 0;
    }
});
const from = computed(
    () =>
        new Date(
            (packageSelectStore.selectedPackages[0] as HotelPackage).fromDate ||
                ''
        )
);
const to = computed(
    () =>
        new Date(
            (packageSelectStore.selectedPackages[0] as HotelPackage).toDate ||
                ''
        )
);
const priceInfo = computed(() => {
    const info = { configs: {} as Record<string, DayConfig> };
    Object.keys(prices.value).forEach(key => {
        const p = prices.value[key];
        info.configs[key] = {
            label: p.isFull
                ? 'تکمیل'
                : !p.price
                  ? '-'
                  : `${formatMoney(p.price, {
                        includeUnit: false,
                        unit: 'KToman',
                    })} ت`,
            color: p.isFull ? { main: 'var(--red-400)' } : {},
            labelColor: p.isFull ? { main: 'var(--red-400)' } : {},
            background: p.isFull
                ? {
                      selected: 'var(--red-400)',
                      hover: 'var(--red-300)',
                  }
                : {},
        };
    });
    return info;
});
const getPrices = async () => {
    loadingPrice.value = true;
    prices.value = await hotelModule.getInventoryPriceList(roomId);
    loadingPrice.value = false;
};
const close = () => emit('close');
onMounted(() => getPrices());
</script>

<template>
    <div class="datepicker-container" @click.stop>
        <div class="header">
            <div class="icon" />
            <mr-typography type="h4" align="center" weight="500">
                تقویم موجودی
            </mr-typography>
            <div class="icon close" @click="close">
                <mr-icon :icon="faXmark" size="large" color="var(--gray-600)" />
            </div>
        </div>
        <div class="datepicker-body datepicker-wrapper">
            <ClientOnly>
                <DatepickerCalendar
                    class="inventory-calendar"
                    :model-value="{ start: from, end: to }"
                    read-only
                    range
                    :day-config="priceInfo.configs"
                    :month-count="1"
                    :min="new Date()"
                    :current-calendar
                    :loading="loadingPrice"
                    disable-mobile-month-list
                />
            </ClientOnly>
        </div>
        <mr-button class="close-button" full-width @click="close">
            بستن
        </mr-button>
    </div>
</template>

<style lang="scss" scoped>
.datepicker-container {
    display: flex;
    flex-direction: column;
    text-align: center;
    padding: 16px 0;
    background: white;
    border-radius: 10px;
    height: 541px;
}

.datepicker-wrapper {
    flex-grow: 1;
    position: relative !important;
    &:deep() {
        .month-list {
            transform: scale(0.9);
        }
    }
}

.header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 31px;
    padding: 0 21px;
    .icon {
        display: flex;
        align-items: center;
        justify-content: space-between;
        height: 14px;
        width: 14px;
        &.close {
            cursor: pointer;
        }
    }
}

.close-button {
    flex-shrink: 0;
    max-width: 200px;
    margin: auto auto 0;
}

.inventory-calendar :deep() {
    .day-wrapper.full-capacity {
        .day-container {
            color: usePalette($p-red);
            .label {
                color: usePalette($p-red);
            }
        }
        &.selected,
        &:hover:not(.disable) {
            .day-container {
                background: usePalette($p-red);
                color: white;
                .label {
                    color: white;
                }
            }
        }
    }
}

@media (max-width: $app-mobile-max-width) {
    .datepicker-wrapper {
        &:deep() {
            .month-list {
                transform: scale(0.8);
            }
        }
    }
}
</style>
